import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { mergeDimensionalFamilyData, resolveDimensionalTemplate, summarizeDimensionalEnvironment } from './dimensional-calibration-profile';
import {
  certificateImageAsDataUrl,
  drawCertificateFooter,
  drawCertificateHeader,
  drawCertificateLegalText,
  drawReferenceStandardsTable,
} from './certificate-pdf-layout';

const LOGO_URL = '/logo-calins-color-hd.png';
const SIGNATURE_URL = new URL('../assets/firma-daniel-kemeny.png', import.meta.url).href;

function numberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function pdfUnit(value) {
  return String(value || '').replaceAll('°', 'deg').replaceAll('µ', 'u');
}

function fmt(value, decimals = 3) {
  const number = numberOrNull(value);
  return number === null ? '-' : number.toFixed(decimals).replace('.', ',');
}

function fmtPct(value, decimals = 3) {
  const number = numberOrNull(value);
  return number === null ? '-' : `${number.toFixed(decimals).replace('.', ',')}%`;
}

function dmy(value) {
  if (!value) return '-';
  const text = String(value);
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T12:00:00` : text);
  if (Number.isNaN(date.getTime())) return '-';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

function certificateNumber(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (/^\d+$/.test(text)) return text;
    const match = text.match(/OS-(\d+)-(\d+)$/i);
    if (match) return `${match[1]}${match[2]}`;
  }
  return '-';
}

async function imageAsDataUrl(url) {
  return certificateImageAsDataUrl(url);
}

function drawHeader(doc, { logo, number, page, pages, first = false }) {
  drawCertificateHeader(doc, { logo, number, page, pages, first });
}

function drawFooter(doc, page, pages) {
  drawCertificateFooter(doc, page, pages);
}

function withOpacity(doc, alpha) {
  if (typeof doc.addGState !== 'function' || typeof doc.setGState !== 'function') return false;
  try {
    const key = `DIM_WM_${Math.round(alpha * 100)}`;
    doc.addGState(key, { opacity: alpha, strokeOpacity: alpha });
    doc.setGState(key);
    return true;
  } catch {
    return false;
  }
}

function drawSimulationWatermark(doc) {
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  doc.saveGraphicsState();
  const transparent = withOpacity(doc, 0.06);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(34);
  doc.setTextColor(transparent ? 200 : 245, transparent ? 0 : 220, transparent ? 0 : 221);
  doc.text('SIMULACIÓN - NO VÁLIDO', width / 2, height / 2, { align: 'center', angle: 35 });
  if (transparent) withOpacity(doc, 1);
  doc.restoreGraphicsState();
  doc.setTextColor(0, 0, 0);
}

function drawTrainingNotice(doc) {
  const width = doc.internal.pageSize.getWidth();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.2);
  doc.setTextColor(200, 0, 0);
  doc.text('DOCUMENTO DE CAPACITACIÓN - SIN VALIDEZ METROLÓGICA, COMERCIAL NI LEGAL', width / 2, 281, { align: 'center' });
  doc.setTextColor(0, 0, 0);
}

function drawBottomLegal(doc) {
  const lines = [
    'Los resultados contenidos en el presente certificado se refieren exclusivamente a los equipos ó instrumentos sometidos a',
    'calibración ó medición, así como al momento y condiciones en que se realizaron las mediciones.',
    'El Laboratorio que lo emite no se responsabiliza de los perjuicios que puedan derivarse del uso inadecuado de este',
    'certificado.',
  ];
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  lines.forEach((line, index) => doc.text(line, 14, 262 + index * 4.6));
}

function drawLegalParagraph(doc) {
  drawCertificateLegalText(doc, 66, { x: 18, width: 174, fontSize: 10 });
}

function familyProcedure(session) {
  return mergeDimensionalFamilyData(session.family_data, session.template_code).procedure
    || resolveDimensionalTemplate(session.template_code).procedure;
}

function instrumentObjectLabel(session, instrument, template) {
  const value = String(session.instrument_object || '').trim();
  if (!value || ['dimensional', 'calibración dimensional', 'calibracion dimensional'].includes(value.toLowerCase())) {
    return instrument?.type || template.objectFallback;
  }
  return value;
}

function drawPageOne(doc, { session, instrument }) {
  const template = resolveDimensionalTemplate(session.template_code);
  const familyData = mergeDimensionalFamilyData(session.family_data, session.template_code);
  const environmentSummary = summarizeDimensionalEnvironment(familyData.environment_readings, familyData.reference_temperature_c);
  const unit = pdfUnit(session.instrument_unit || template.unit);
  drawLegalParagraph(doc);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('ESPECIFICACIONES', 14, 113);
  const rowHeight = 5.2;
  const startY = 123;
  const leftRows = [
    ['Objeto:', instrumentObjectLabel(session, instrument, template)],
    ['Fabricante:', session.instrument_brand || instrument?.brand || '-'],
    ['Modelo:', session.instrument_model || instrument?.model || '-'],
    ['Serie Número:', session.instrument_serial || instrument?.serial || '-'],
    ['TAG:', session.instrument_tag || instrument?.tag || '-'],
    ['Determinaciones requeridas:', 'Calibración'],
    ['Fecha de Calibración o Medición:', dmy(session.calibration_date)],
  ];
  const rightRows = [
    ['Ubicación:', session.instrument_location || '-'],
    ['Tolerancia:', session.instrument_class == null ? '-' : `± ${fmt(session.instrument_class, 3)} [${unit}]`],
    ['Resolución:', `${fmt(session.instrument_resolution, 3)} [${unit}]`],
    ['Menor División:', `${fmt(session.instrument_resolution, 3)} [${unit}]`],
    ['Intervalo:', `${fmt(session.instrument_range_min, 0)} a ${fmt(session.instrument_range_max, 0)} [${unit}]`],
    ['Fecha de emisión de Informe:', dmy(session.report_issue_date)],
    ['Validez :', dmy(session.valid_until)],
    ['Procedimiento de calibración:', familyProcedure(session)],
  ];

  leftRows.forEach(([label, value], index) => {
    const y = startY + index * rowHeight;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(label, 14, y);
    doc.setFont('helvetica', 'normal');
    const valueText = String(value ?? '-');
    doc.setFontSize(10);
    const fittedSize = Math.min(10, 10 * 38 / Math.max(38, doc.getTextWidth(valueText)));
    doc.setFontSize(fittedSize);
    doc.text(valueText, 70, y);
  });
  rightRows.forEach(([label, value], index) => {
    const y = startY + index * rowHeight;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(label, 110, y);
    doc.setFont('helvetica', 'normal');
    const valueText = String(value ?? '-');
    if (label === 'Procedimiento de calibración:') {
      doc.setFontSize(8.5);
      doc.text(valueText, 110, y + 4.4, { maxWidth: 86 });
    } else {
      const fittedSize = Math.min(10, 10 * 48 / Math.max(48, doc.getTextWidth(valueText)));
      doc.setFontSize(fittedSize);
      doc.text(valueText, 196, y, { align: 'right' });
    }
  });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('CLIENTE', 14, 175);
  const clientRows = [
    ['Razón Social:', session.client_name || '-'],
    ['Dirección:', session.client_address || '-'],
    ['Ciudad/Dpto.:', session.client_city || '-'],
    ['Provincia:', session.client_province || '-'],
  ];
  clientRows.forEach(([label, value], index) => {
    const y = 185 + index * rowHeight;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(label, 60, y, { align: 'right' });
    doc.setFont('helvetica', index === 0 ? 'bolditalic' : 'normal');
    doc.text(String(value), 92, y);
  });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('CONDICIONES AMBIENTALES', 14, 217);
  const environment = [
    ['Presión atmosférica:', fmt(environmentSummary.ambient_pressure_avg ?? session.ambient_pressure_avg ?? session.ambient_pressure, 1), '[hPa] ± 1 [hPa]'],
    ['Temperatura Ambiente:', fmt(environmentSummary.ambient_temp_avg ?? session.ambient_temp_avg ?? session.ambient_temp, 1), '[°C] ± 1 [°C]'],
    ['Humedad Relativa:', fmt(environmentSummary.ambient_humidity_avg ?? session.ambient_humidity_avg ?? session.ambient_humidity, 1), '[% H.R.] ± 3 [% H.R.]'],
  ];
  environment.forEach(([label, value, suffix], index) => {
    const y = 228 + index * rowHeight;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(label, 74, y, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.text(value, 128, y, { align: 'right' });
    doc.text(suffix, 156, y);
  });
  drawBottomLegal(doc);
}

function nominalByBlock(points = []) {
  const nominal = new Map();
  points.forEach((point) => {
    const key = `${point.block_id}:${point.point_no}`;
    if (!nominal.has(key)) nominal.set(key, point.setpoint);
  });
  return nominal;
}

function maxMetrics(results) {
  return results.reduce((summary, result) => ({
    error: Math.max(summary.error, Math.abs(numberOrNull(result.error_abs) || 0)),
    uncertainty: Math.max(summary.uncertainty, Math.abs(numberOrNull(result.u_reported) || 0)),
  }), { error: 0, uncertainty: 0 });
}

function averageFinite(values = []) {
  const finite = values.map(numberOrNull).filter((value) => value !== null);
  return finite.length ? finite.reduce((sum, value) => sum + value, 0) / finite.length : null;
}

function decimalsFromResolution(value, fallback = 3) {
  const resolution = Math.abs(numberOrNull(value) || 0);
  if (!resolution) return fallback;
  return Math.max(0, Math.min(5, Math.ceil(-Math.log10(resolution) - 1e-9)));
}

function drawDeviationChart(doc, { x, y, width, height, results, tolerance, unit }) {
  const plotX0 = x + 18;
  const plotX1 = x + width - 10;
  const plotY0 = y + 12;
  const plotY1 = y + height - 16;
  const maxObserved = results.reduce((maximum, result) => Math.max(
    maximum,
    Math.abs(numberOrNull(result.error_abs) || 0) + Math.abs(numberOrNull(result.u_reported) || 0),
  ), 0);
  const extent = Math.max(maxObserved, Math.abs(numberOrNull(tolerance) || 0), 0.001) * 1.4;
  const minY = -extent;
  const maxY = extent;
  const count = Math.max(1, results.length);
  const toX = (index) => plotX0 + ((index + 1) * (plotX1 - plotX0)) / count;
  const toY = (value) => plotY1 - ((value - minY) / (maxY - minY)) * (plotY1 - plotY0);

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.rect(x, y, width, height);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  for (let index = 0; index <= 6; index += 1) {
    const value = minY + ((maxY - minY) * index) / 6;
    const lineY = toY(value);
    doc.setDrawColor(170, 170, 170);
    doc.setLineWidth(0.1);
    doc.line(plotX0, lineY, plotX1, lineY);
    doc.setTextColor(0, 0, 0);
    doc.text(fmt(value, Math.abs(extent) < 0.1 ? 3 : 2), plotX0 - 2, lineY + 1.5, { align: 'right' });
  }
  for (let index = 0; index <= count; index += 1) {
    const lineX = plotX0 + (index * (plotX1 - plotX0)) / count;
    doc.setDrawColor(120, 120, 120);
    doc.line(lineX, plotY1, lineX, plotY1 + 2);
    doc.text(String(index), lineX, plotY1 + 7, { align: 'center' });
  }

  const absoluteTolerance = Math.abs(numberOrNull(tolerance) || 0);
  if (absoluteTolerance > 0) {
    doc.setDrawColor(200, 0, 0);
    doc.setLineWidth(0.45);
    doc.line(toX(0) - 2, toY(absoluteTolerance), toX(count - 1) + 2, toY(absoluteTolerance));
    doc.line(toX(0) - 2, toY(-absoluteTolerance), toX(count - 1) + 2, toY(-absoluteTolerance));
  }

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  results.forEach((result, index) => {
    const error = numberOrNull(result.error_abs) || 0;
    const uncertainty = Math.abs(numberOrNull(result.u_reported) || 0);
    const lineX = toX(index);
    const low = toY(error - uncertainty);
    const high = toY(error + uncertainty);
    doc.line(lineX, low, lineX, high);
    doc.line(lineX - 1.2, low, lineX + 1.2, low);
    doc.line(lineX - 1.2, high, lineX + 1.2, high);
  });
  doc.setDrawColor(0, 70, 140);
  doc.setLineWidth(0.5);
  results.forEach((result, index) => {
    if (index === 0) return;
    doc.line(
      toX(index - 1),
      toY(numberOrNull(results[index - 1].error_abs) || 0),
      toX(index),
      toY(numberOrNull(result.error_abs) || 0),
    );
  });
  doc.setFillColor(110, 180, 110);
  results.forEach((result, index) => {
    doc.circle(toX(index), toY(numberOrNull(result.error_abs) || 0), 1.8, 'FD');
  });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(`Puntos de Calibración - error [${unit}]`, x + width / 2, y + height - 4, { align: 'center' });
}

function drawResultPage(doc, { session, block, points, results }) {
  const template = resolveDimensionalTemplate(session.template_code);
  const unit = pdfUnit(session.instrument_unit || template.unit);
  const nominal = nominalByBlock(points);
  const blockResults = results
    .filter((result) => result.block_id === block.id)
    .sort((left, right) => left.point_no - right.point_no);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('RESULTADOS OBTENIDOS', 14, 45);

  autoTable(doc, {
    startY: 52,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: { fontSize: 9, halign: 'center', valign: 'middle', cellPadding: 1.7, lineWidth: 0.1, lineColor: [0, 0, 0] },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold' },
    head: [
      ['Pto. de\nMedición', 'Patrón', 'Instrumento', 'Histéresis', 'Error Instr.', 'Error Instr.', 'Incert.\nExpandida'],
      ['', `[${unit}]`, `[${unit}]`, `[${unit}]`, `[${unit}]`, '%', `[${unit}]`],
    ],
    body: blockResults.map((result, index) => [
      String(result.point_no ?? index + 1),
      fmt(result.pattern_mean ?? nominal.get(`${block.id}:${result.point_no}`)),
      fmt(result.instrument_mean),
      fmt(result.hysteresis),
      fmt(result.error_abs),
      fmtPct(result.error_pct),
      fmt(result.u_reported),
    ]),
  });

  const metrics = maxMetrics(blockResults);
  const metricY = (doc.lastAutoTable?.finalY || 110) + 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Error sistemático Mayor:', 22, metricY);
  doc.text('Incertidumbre expandida', 22, metricY + 7);
  doc.setFont('helvetica', 'normal');
  doc.text(fmt(metrics.error), 104, metricY, { align: 'right' });
  doc.text(`[${unit}]`, 114, metricY);
  doc.text(fmt(metrics.uncertainty), 104, metricY + 7, { align: 'right' });
  doc.text(`[${unit}]`, 114, metricY + 7);

  const chartTitleY = metricY + 30;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('DESVIACIÓN', 14, chartTitleY);
  drawDeviationChart(doc, {
    x: 14,
    y: chartTitleY + 5,
    width: 186,
    height: 85,
    results: blockResults,
    tolerance: session.instrument_class,
    unit,
  });
  drawBottomLegal(doc);
}

function drawCaliperResultsPage(doc, { session, blocks, points, results }) {
  const template = resolveDimensionalTemplate(session.template_code);
  const unit = pdfUnit(session.instrument_unit || template.unit);
  const readingDecimals = decimalsFromResolution(session.instrument_resolution, 3);
  const functions = [
    { key: 'outside', label: 'A' },
    { key: 'inside', label: 'B' },
    { key: 'depth', label: 'C' },
    { key: 'step', label: 'E' },
  ].map((item) => ({
    ...item,
    block: blocks.find((block) => block.block_key === item.key),
  }));
  const pointNumbers = [...new Set([
    ...points.map((point) => Number(point.point_no)),
    ...results.map((result) => Number(result.point_no)),
  ].filter(Number.isFinite))].sort((left, right) => left - right);
  const rawPoint = (block, pointNo, seriesNo) => points.find((point) => (
    block
    && String(point.block_id) === String(block.id)
    && Number(point.point_no) === pointNo
    && Number(point.series_no) === seriesNo
  ));
  const resultFor = (block, pointNo) => results.find((result) => (
    block
    && String(result.block_id) === String(block.id)
    && Number(result.point_no) === pointNo
  ));
  const nominalFor = (pointNo) => {
    for (const item of functions) {
      const source = rawPoint(item.block, pointNo, 1) || rawPoint(item.block, pointNo, 2);
      const value = numberOrNull(source?.setpoint ?? source?.pattern_reading);
      if (value !== null) return value;
    }
    return null;
  };

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('RESULTADOS OBTENIDOS', 14, 42);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.8);
  doc.text('Error = indicación del instrumento - valor corregido del patrón.', 14, 47);

  autoTable(doc, {
    startY: 51,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: {
      fontSize: 7.2, halign: 'center', valign: 'middle', cellPadding: 0.8,
      lineWidth: 0.12, lineColor: [30, 30, 30], minCellHeight: 4.5,
    },
    headStyles: { fillColor: [180, 202, 226], textColor: [0, 0, 0], fontStyle: 'bold' },
    head: [
      [
        { content: 'Punto #', rowSpan: 3 },
        { content: 'LONG.\nPATRÓN', rowSpan: 2 },
        { content: 'PROMEDIO LECTURAS', colSpan: 8 },
      ],
      ['A1', 'B1', 'C1', 'E1', 'A2', 'B2', 'C2', 'E2'],
      [`[${unit}]`, ...Array.from({ length: 8 }, () => `[${unit}]`)],
    ],
    body: pointNumbers.map((pointNo) => [
      String(pointNo),
      fmt(nominalFor(pointNo), 3),
      ...[1, 2].flatMap((seriesNo) => functions.map((item) => (
        fmt(rawPoint(item.block, pointNo, seriesNo)?.instrument_reading, readingDecimals)
      ))),
    ]),
    columnStyles: {
      0: { cellWidth: 13 },
      1: { cellWidth: 22 },
      2: { cellWidth: 18.375 }, 3: { cellWidth: 18.375 }, 4: { cellWidth: 18.375 }, 5: { cellWidth: 18.375 },
      6: { cellWidth: 18.375 }, 7: { cellWidth: 18.375 }, 8: { cellWidth: 18.375 }, 9: { cellWidth: 18.375 },
    },
  });

  const secondTableY = (doc.lastAutoTable?.finalY || 100) + 13;
  autoTable(doc, {
    startY: secondTableY,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: {
      fontSize: 6.7, halign: 'center', valign: 'middle', cellPadding: 0.65,
      lineWidth: 0.12, lineColor: [30, 30, 30], minCellHeight: 4.4,
    },
    headStyles: { fillColor: [180, 202, 226], textColor: [0, 0, 0], fontStyle: 'bold' },
    head: [
      [
        { content: 'Punto de\ncalibración', rowSpan: 3 },
        { content: 'LONG.\nPATRÓN', rowSpan: 2 },
        { content: 'MEDIA INSTRUMENTO', colSpan: 4 },
        { content: 'ERROR', colSpan: 4 },
        { content: 'REPETIBILIDAD', colSpan: 2 },
        { content: 'INCERT.\nEXPAND.', rowSpan: 2 },
      ],
      ['A', 'B', 'C', 'E', 'A', 'B', 'C', 'E', 'A', 'B'],
      [
        `[${unit}]`,
        ...Array.from({ length: 4 }, () => `[${unit}]`),
        ...Array.from({ length: 4 }, () => '%'),
        `[${unit}]`, `[${unit}]`, `[${unit}]`,
      ],
    ],
    body: pointNumbers.map((pointNo) => {
      const pointResults = Object.fromEntries(functions.map((item) => [item.label, resultFor(item.block, pointNo)]));
      return [
        String(pointNo),
        fmt(nominalFor(pointNo), 3),
        ...functions.map((item) => fmt(pointResults[item.label]?.instrument_mean, readingDecimals)),
        ...functions.map((item) => fmtPct(pointResults[item.label]?.error_pct, 3)),
        fmt(pointResults.A?.repeatability, 4),
        fmt(pointResults.B?.repeatability, 4),
        fmt(averageFinite(functions.map((item) => pointResults[item.label]?.u_reported)), 4),
      ];
    }),
    columnStyles: {
      0: { cellWidth: 17 },
      1: { cellWidth: 21 },
      2: { cellWidth: 14 }, 3: { cellWidth: 14 }, 4: { cellWidth: 14 }, 5: { cellWidth: 14 },
      6: { cellWidth: 13 }, 7: { cellWidth: 13 }, 8: { cellWidth: 13 }, 9: { cellWidth: 13 },
      10: { cellWidth: 10.5 }, 11: { cellWidth: 10.5 },
      12: { cellWidth: 15 },
    },
  });

  const maxErrorPct = Math.max(0, ...results.map((result) => Math.abs(numberOrNull(result.error_pct) || 0)));
  const maxUncertainty = maxMetrics(results).uncertainty;
  const summaryY = (doc.lastAutoTable?.finalY || 175) + 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text('Error sistemático mayor [%]:', 22, summaryY);
  doc.text('Incertidumbre expandida máxima:', 22, summaryY + 6);
  doc.setFont('helvetica', 'normal');
  doc.text(`${fmt(maxErrorPct, 3)} %`, 94, summaryY, { align: 'right' });
  doc.text(`${fmt(maxUncertainty, 4)} ${unit}`, 94, summaryY + 6, { align: 'right' });
  drawBottomLegal(doc);
}

function drawConsolidatedResultsPage(doc, { session, familyData, blocks, points, results }) {
  const template = resolveDimensionalTemplate(session.template_code);
  if (template.profileKey === 'caliper') {
    drawCaliperResultsPage(doc, { session, blocks, points, results });
    return;
  }
  const unit = pdfUnit(session.instrument_unit || template.unit);
  const nominal = nominalByBlock(points);
  let cursorY = 51;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('RESULTADOS OBTENIDOS', 14, 42);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.8);
  doc.text('Error = indicación del instrumento - valor corregido del patrón.', 14, 47);

  blocks.forEach((block) => {
    const blockResults = results
      .filter((result) => result.block_id === block.id)
      .sort((left, right) => left.point_no - right.point_no);
    if (!blockResults.length) return;

    const blockLabel = block.display_name || block.label || block.block_name || block.block_key || 'Medición dimensional';
    doc.setFillColor(226, 239, 243);
    doc.rect(14, cursorY - 3.5, 182, 6, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.3);
    doc.text(String(blockLabel), 16, cursorY + 0.7);

    autoTable(doc, {
      startY: cursorY + 3.5,
      margin: { left: 14, right: 14 },
      theme: 'grid',
      styles: {
        fontSize: 6.3, halign: 'center', valign: 'middle', cellPadding: 0.65,
        lineWidth: 0.08, lineColor: [70, 70, 70], minCellHeight: 3.7,
      },
      headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold', minCellHeight: 6.4 },
      head: [[
        'Pto.', `Patrón\n[${unit}]`, `Instrumento\n[${unit}]`, `Histéresis\n[${unit}]`,
        `Error\n[${unit}]`, 'Error\n[%]', `U (k=2)\n[${unit}]`,
      ]],
      body: blockResults.map((result, index) => [
          String(result.point_no ?? index + 1),
          fmt(result.pattern_mean ?? nominal.get(`${block.id}:${result.point_no}`), 5),
          fmt(result.instrument_mean, 3),
          numberOrNull(result.hysteresis) === null ? '-' : fmt(result.hysteresis, 4),
          fmt(result.error_abs, 4),
          fmtPct(result.error_pct, 3),
          fmt(result.u_reported, 4),
        ]),
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 31 },
        2: { cellWidth: 31 },
        3: { cellWidth: 28 },
        4: { cellWidth: 28 },
        5: { cellWidth: 24 },
        6: { cellWidth: 28 },
      },
    });

    const metrics = maxMetrics(blockResults);
    cursorY = (doc.lastAutoTable?.finalY || cursorY + 32) + 4.2;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.8);
    doc.text(`Mayor |error|: ${fmt(metrics.error, 4)} ${unit}   ·   U máxima: ${fmt(metrics.uncertainty, 4)} ${unit}`, 16, cursorY);
    cursorY += 8.3;
  });

  drawBottomLegal(doc);
}

function referenceSummary(references) {
  if (!references.length) return { description: '-', serial: '-', certificate: '-', date: '-' };
  const assets = references.map((reference) => reference.asset || {});
  const certificates = references.map((reference) => reference.certificate || {});
  return {
    description: assets.map((asset) => asset.brand_model || asset.name || asset.code || '-').join(' / '),
    serial: assets.map((asset) => asset.serial || '-').join(' / '),
    certificate: certificates.map((certificate) => certificate.certificate_no || '-').join(' / '),
    date: certificates.map((certificate) => dmy(certificate.calibration_date)).join(' / '),
  };
}

function drawMethodPage(doc, { session, familyData, signature, simulation }) {
  const template = resolveDimensionalTemplate(session.template_code);
  const references = familyData.standard_configuration?.references || [];
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('OBSERVACIONES', 14, 45);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.text('El valor de la incertidumbre expandida fue calculado multiplicando la incertidumbre estándar', 14, 58);
  doc.text('combinada por el factor de cobertura k=2 correspondiente a un nivel de confianza cercano al 95%.', 14, 64);
  const observation = simulation
    ? 'SIMULACIÓN PARA CAPACITACIÓN. DOCUMENTO NO VÁLIDO PARA USO METROLÓGICO, COMERCIAL NI LEGAL.'
    : String(session.notes || 'Sin observaciones adicionales.');
  const observationLines = doc.splitTextToSize(observation, 186);
  doc.text(observationLines, 14, 76);

  const methodTitleY = 88 + Math.max(0, observationLines.length - 1) * 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('MÉTODO Y PROCEDIMIENTO', 14, methodTitleY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  const methodLines = doc.splitTextToSize(`${familyData.method || template.method} ${familyData.procedure || template.procedure}`, 186);
  doc.text(methodLines, 14, methodTitleY + 8);

  const patternTableEnd = drawReferenceStandardsTable(doc, {
    startY: methodTitleY + 14 + Math.max(0, methodLines.length - 1) * 5,
    references: references.map((snapshot) => ({
      ...snapshot,
      usage: 'Referencia',
    })),
  });

  const definitions = [
    ['Posición del instrumento calibrado', session.instrument_position || 'Posición estable y alineada con el patrón.'],
    ['Error del instrumento', 'Es la diferencia entre el promedio de las indicaciones del instrumento y el valor corregido del patrón para un mismo punto.'],
    ['Valor indicado por el patrón', 'Es el valor de referencia materializado o indicado por el patrón.'],
    ['Valor indicado por el instrumento', 'Es el valor indicado por el instrumento sometido a calibración.'],
  ];
  if (template.measurementMode === 'bidirectional') {
    definitions.push(['Histéresis', 'Es la diferencia de indicación para un mismo punto entre las secuencias ascendente y descendente.']);
  }
  let y = patternTableEnd + 9;
  definitions.forEach(([heading, body]) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11.2);
    doc.text(heading, 14, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10.2);
    const lines = doc.splitTextToSize(body, 186);
    doc.text(lines, 14, y + 6);
    y += 12 + Math.max(0, lines.length - 1) * 5.2;
  });
  if (signature && !simulation && y < 233) doc.addImage(signature, 'PNG', 78, Math.max(219, y + 2), 44, 25, undefined, 'FAST');
  drawBottomLegal(doc);
}

export async function generateDimensionalNoSacCertificatePdf(
  { session, instrument, blocks = [], points = [], results = [] },
  options = {},
) {
  if (!session) throw new Error('La sesión es requerida.');
  if (!results.length) throw new Error('No hay resultados para certificar.');

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const [logo, signature] = await Promise.all([
    imageAsDataUrl(options.logoUrl || LOGO_URL),
    imageAsDataUrl(options.signatureUrl || SIGNATURE_URL),
  ]);
  const number = certificateNumber(
    options.serviceOrderCode,
    session.service_order_code,
    session.certificate_number,
    instrument?.lab_id,
  );
  const familyData = mergeDimensionalFamilyData(session.family_data, session.template_code);
  const simulation = Boolean(options.simulation || session.is_simulation);

  if (simulation) drawSimulationWatermark(doc);
  drawPageOne(doc, { session, instrument });
  doc.addPage('a4', 'portrait');
  if (simulation) drawSimulationWatermark(doc);
  drawConsolidatedResultsPage(doc, { session, familyData, blocks, points, results });
  doc.addPage('a4', 'portrait');
  if (simulation) drawSimulationWatermark(doc);
  drawMethodPage(doc, { session, familyData, signature, simulation });

  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    drawHeader(doc, { logo, number, page, pages, first: page === 1 });
    if (simulation) drawTrainingNotice(doc);
    drawFooter(doc, page, pages);
  }

  if (options.returnBlob) return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
  doc.save(options.filename || `${number !== '-' ? number : 'R-DT-PC-DIM'}.pdf`);
  return null;
}

